























































































import { defineComponent } from '@vue/composition-api';
import { mapGetters, mapState } from 'vuex';
import { HeatingSystemSummary } from '@/features/domain-ui/heating-system-list-table/model';
import {
  ATRegulationMetadataStatus,
  getHeatingSystemsAndSpots,
  GK4HeizungsprofiMetadataStatus,
  isComponentMetadataCompleted,
  removeLabelFromName,
} from '@/util/heating-systems';
import {
  HeatingSystem,
  HeatingSystemComponent,
  HeatingSystemMetadata,
  HeatingSystemMetadataKey,
  HeatingSystemMetadataResponse,
} from '@/features/core/store/page-modules/heating-systems-metadata/heating-systems-metadata.module';
import HeatingSystemsMetadataForm from './HeatingSystemsMetadataForm.vue';
import HeatingSystemsMetadataApplySettings from './HeatingSystemsMetadataApplySettings.vue';
import HeatingSystemsMetadataAudit from './HeatingSystemsMetadataAudit.vue';
import { isString } from 'lodash';
import Spinner from '@/components/clickables/Spinner.vue';
import { PaginationQueryStringMixin } from '@/features/core/components/mixins/pagination-query-string';
import GroupsMixin from '@/features/core/components/mixins/groups';
import { HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items } from '@/hsc-api/queries/__generated__/HeatingSystemCollectionWithPaginationQuery';
import heatingSystemsListQuery from '@/hsc-api/queries/HeatingSystemCollectionWithPaginationQuery.gql';
import { APOLLO_CLIENT } from '@/features/core/container/model';
import { HeatingSystemHealth } from '@/types/iot-portal';
import {
  AlertHeatingSystemSummaries_statuses,
  AlertHeatingSystemSummariesVariables,
} from '@/hsc-api/queries/__generated__/AlertHeatingSystemSummaries';
import alertHeatingSystemSummariesQuery from '@/hsc-api/queries/AlertTicketsSummaryByHeatingSystemIdQuery.gql';
import modbusMappingsQuery from '@/features/app-heating-system/views/tree-node/get-modbus-mappings-cust-id.gql';
import { ModbusMappings, ModbusMappings_modbusMappingConfig } from '../__generated__/ModbusMappings';
import getTreeNodeCustomerIdQuery from '@/features/app-heating-system/views/tree-node/get-tree-node-customer-id.gql';
import {
  GetTreeNodeCustomerId,
  GetTreeNodeCustomerIdVariables,
} from '@/features/app-heating-system/views/tree-node/__generated__/GetTreeNodeCustomerId';
import { AT_REGULATION_FILTER } from '@/env';

type HeatingSystemList = HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items;
type ComponentType = {
  component: string;
  type: string;
  groupName: string;
  hasEditableRegister?: boolean; // Indicates if the component has editable registers
};

export default defineComponent({
  components: {
    HeatingSystemsMetadataForm,
    HeatingSystemsMetadataApplySettings,
    HeatingSystemsMetadataAudit,
    Spinner,
  },
  mixins: [GroupsMixin, PaginationQueryStringMixin],
  props: {
    treeNodeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoadingMetadata: false,
      summariesLoading: false,
      isInitializingFilters: true,
      customerId: null as string | null,
      metadata: {} as HeatingSystemMetadataKey,
      editableRegisterNames: [] as string[],
      filters: {
        searchInput: '',
        metadataStatus: null,
      },
      GK4HeizungsprofiMetadataStatusOptions: [
        {
          label: 'Pflichtangaben vollständig',
          value: GK4HeizungsprofiMetadataStatus.MANDATORY_INFORMATION_COMPLETE,
        },
        {
          label: 'Pflichtangaben unvollständig',
          value: GK4HeizungsprofiMetadataStatus.MANDATORY_INFORMATION_INCOMPLETE,
        },
      ],
      ATRegulationMetadataStatusOptions: [
        {
          label: 'Ja',
          value: ATRegulationMetadataStatus.TRUE,
        },
        {
          label: 'Nein',
          value: ATRegulationMetadataStatus.FALSE,
        },
      ],
      previousActiveComponent: null as HeatingSystemComponent | null | undefined,
      heatingSystemsData: [] as HeatingSystemSummary[],
      nonMandatoryComponentTypes: ['BUFFER'],
    };
  },
  computed: {
    ...mapGetters(['openModal', 'openApplySettings', 'openAuditView', 'selectedCustomerId']),
    ...mapState({
      activeHeatingSystemAtRegulationConfig: (state: any) =>
        state.HeatingSystemsMetadataModule.activeHeatingSystemAtRegulationConfig,
      atRegulationConfigStatusPerComponent: (state: any) =>
        state.HeatingSystemsMetadataModule.atRegulationConfigStatusPerComponent,
    }),
    showATRegulationControl(): boolean {
      const filters = (AT_REGULATION_FILTER ?? []).map((f) => f.trim()).filter(Boolean);
      const cidStr = String(this.customerId ?? '').trim();
      return filters.includes(cidStr);
    },
    heatingSystems(): HeatingSystem[] {
      return this.$store.getters.heatingSystems;
    },
    heatingSystemsMetadata(): HeatingSystemMetadataResponse[] | null {
      return this.$store.getters.heatingSystemsMetadata;
    },
    heatingSystemsComponents(): HeatingSystemComponent[] {
      return this.$store.getters.heatingSystemsComponents;
    },
    activeHeatingSystem(): HeatingSystem | undefined {
      return this.$store.getters.activeHeatingSystem;
    },
    activeHeatingSystemComponent(): HeatingSystemComponent | undefined {
      return this.$store.getters.activeHeatingSystemComponent;
    },
    filteredHeatingSystems(): HeatingSystem[] {
      let heatingSystems: HeatingSystem[] = [];
      if (this.filters.searchInput) {
        const result = this.heatingSystems.filter((heatingSystem: HeatingSystem) =>
          heatingSystem.street?.toLocaleLowerCase().includes(this.filters.searchInput.toLocaleLowerCase()),
        );
        heatingSystems = result.length > 0 ? result : this.heatingSystems;
      } else if (this.filters.metadataStatus) {
        heatingSystems = this.filterHeatingSystemsByStatus();
      } else {
        heatingSystems = this.heatingSystems;
      }

      return heatingSystems.sort((a, b) => {
        const streetA = a.street ?? '';
        const streetB = b.street ?? '';
        return streetA.localeCompare(streetB, undefined, { numeric: true, sensitivity: 'base' });
      });
    },
    filteredComponents(): HeatingSystemComponent[] {
      let components = this.heatingSystemsComponents.filter((component) =>
        this.allowedComponentTypes().includes(component.type),
      );

      // If AT regulation control is active, only include components, that have editable registers.
      if (this.showATRegulationControl) {
        components = components.filter((c) => c.hasEditableRegister === true);
      }

      if (this.filters.metadataStatus) {
        components = this.filterComponentsByStatus();
      }

      return components.sort((a, b) => a.label.localeCompare(b.label));
    },
    formData(): HeatingSystemMetadata {
      return this.$store.getters.formData;
    },
    spotIdsWithCompletedMetadata(): string[] {
      const spotIds = this.getSpotIdsWithCompletedMetadata();
      const completedMetadata = this.heatingSystemsMetadata?.filter((hsMetadata) =>
        isComponentMetadataCompleted(hsMetadata),
      );
      const result = [...new Set(completedMetadata?.map((hs) => hs.spotId))];

      return [...result, ...spotIds];
    },
  },
  watch: {
    heatingSystemsData(value) {
      const heatingSystems = value.map((item: HeatingSystemSummary) => {
        return {
          ...item,
          active: false,
        };
      });

      // fetchHeatingSystemsList() after calling getHeatingSystemsAndSpots().
      // Avoid overwriting that filtered result here.
      if (!this.showATRegulationControl) {
        this.$store.commit('setHeatingSystems', heatingSystems);
      }

      this.checkParams();
    },
    'filters.metadataStatus'() {
      this.resetComponents();
    },
    async openModal(value: boolean) {
      if (!value) {
        // clear selected values
        this.resetComponents();
        this.resetHeatingSystems();
        this.filters = {
          searchInput: '',
          metadataStatus: null,
        };
      } else {
        this.isInitializingFilters = true;
        this.$store.commit('setHeatingSystems', []);
        await this.getCustomerId();
        this.isInitializingFilters = false;
        await this.getEditableRegisterNames();
        await this.fetchHeatingSystemsList();
      }
    },
    filteredComponents(value: HeatingSystemComponent[]) {
      if (
        this.showATRegulationControl &&
        this.activeHeatingSystemComponent &&
        !value.some((c) => c.value === this.activeHeatingSystemComponent?.value)
      ) {
        this.resetComponents();
        this.$store.commit('openApplySettings', false);
      }
    },
  },
  async mounted() {
    this.isInitializingFilters = true;
    await this.getCustomerId();
    this.isInitializingFilters = false;
    await this.getEditableRegisterNames();
    await this.fetchHeatingSystemsList();
    await this.fetchHeatingSystemsMetadata();
  },
  methods: {
    async getCustomerId(): Promise<string | null> {
      if (this.$route.path.includes('/advanced')) {
        this.customerId = (typeof this.selectedCustomerId === 'string' ? this.selectedCustomerId : null) || null;
      }
      if (!this.customerId) {
        this.customerId = await this.fetchCustomerForTreeNode();
      }
      return this.customerId;
    },
    getSpotIdsWithCompletedMetadata(): string[] {
      const uniqueSpotIds = new Set<string>();

      this.heatingSystems.forEach((hs) => {
        hs.components.heatingSystemMeasurementGroups?.forEach((group) => {
          // non-mandatory component types should be considered completed since no metadata validation is required.
          if (this.nonMandatoryComponentTypes.includes(group.systemComponentTypeName)) {
            group.heatingSystemMeasurements?.forEach((measurement) => {
              uniqueSpotIds.add(measurement.spotId);
            });
          }
        });
      });

      return Array.from(uniqueSpotIds);
    },
    closeModal() {
      this.$store.commit('openModal', false);

      // clear selected values
      this.customerId = null;
      this.resetComponents();
      this.resetHeatingSystems();
    },
    toggleAuditView() {
      if (!this.activeHeatingSystemComponent) {
        this.$store.dispatch('ADD_TOAST_MESSAGES', {
          messages: [{ text: 'Please select a component', class: 'info' }],
        });
      } else {
        this.$store.commit('toggleAuditView');
      }
    },
    async onHeatingSystemChange(id: string) {
      if (!this.editableRegisterNames.length) {
        await this.getEditableRegisterNames();
      }

      if (this.activeHeatingSystem && this.activeHeatingSystem.components.id === id) {
        return;
      }

      if (this.activeHeatingSystemAtRegulationConfig && this.activeHeatingSystemAtRegulationConfig.response) {
        this.$store.commit('resetAtControlRegulationConfig');
      }

      const activeHeatingSystem = this.heatingSystems.find((item) => item.components.id === id);

      // save previously selected component
      this.previousActiveComponent = this.activeHeatingSystemComponent;

      // set active heating system
      this.heatingSystems.forEach((heatingSystem) => {
        heatingSystem.active = heatingSystem.components.id === id ? true : false;
      });

      // close "apply settings" section
      this.$store.commit('openApplySettings', false);

      // get active heating system components
      if (activeHeatingSystem) {
        const spotIdsWithEditableRegisters: string[] = [];
        const components = new Map<string, ComponentType>(
          activeHeatingSystem.components.heatingSystemMeasurementGroups.flatMap(
            ({ heatingSystemMeasurements, systemComponentTypeName, groupName }) => {
              return heatingSystemMeasurements.map((measurement) => {
                const component = removeLabelFromName(measurement.measurementName, measurement.metricLabel);
                if (this.editableRegisterNames.includes(measurement.metricName.toLowerCase())) {
                  spotIdsWithEditableRegisters.push(measurement.spotId);
                }

                return [
                  measurement.spotId,
                  {
                    component,
                    type: systemComponentTypeName,
                    groupName,
                  },
                ];
              });
            },
          ),
        );

        const componentOptions = Array.from(components, ([key, value]) => ({
          value: key,
          label: value.component,
          groupName: value.groupName,
          active: false,
          type: spotIdsWithEditableRegisters.some((spotId) => spotId === key) ? 'HEATING_CONTROL' : value.type,
          hasEditableRegister: spotIdsWithEditableRegisters.some((spotId) => spotId === key),
        }));
        this.$store.commit('setHeatingSystemsComponents', componentOptions);
      }

      // fetch updated HS metadata
      await this.fetchHeatingSystemsMetadata();

      this.setActiveComponentMetadata();
    },
    filterHeatingSystemsByStatus(): HeatingSystem[] {
      if (this.showATRegulationControl) {
        if (this.filters.metadataStatus === ATRegulationMetadataStatus.TRUE) {
          return this.heatingSystems.filter((hs: HeatingSystem) => {
            const spotIds = hs.components.heatingSystemMeasurementGroups.flatMap((g) =>
              g.heatingSystemMeasurements.map((m) => m.spotId),
            );
            return spotIds.some((id) => this.atRegulationConfigStatusPerComponent?.[id] === true);
          });
        }

        if (this.filters.metadataStatus === ATRegulationMetadataStatus.FALSE) {
          return this.heatingSystems.filter((hs: HeatingSystem) => {
            const spotIds = hs.components.heatingSystemMeasurementGroups.flatMap((g) =>
              g.heatingSystemMeasurements.map((m) => m.spotId),
            );
            return spotIds.some((id) => this.atRegulationConfigStatusPerComponent?.[id] === false);
          });
        }
      }
      const heatingSystems = {
        complete: [] as HeatingSystem[],
        incomplete: [] as HeatingSystem[],
      };

      this.heatingSystems.forEach((hs) => {
        const spotIds = Array.from(
          new Set([
            ...hs.components.heatingSystemMeasurementGroups
              .filter((g) => !this.nonMandatoryComponentTypes.includes(g.systemComponentTypeName))
              .flatMap((g) => g.heatingSystemMeasurements.flatMap((m) => m.spotId)),
          ]),
        );

        // populate heating systems where at least one of its components HAS all mandatory fields set
        if (spotIds.some((spotId: string) => this.spotIdsWithCompletedMetadata.includes(spotId))) {
          heatingSystems.complete.push(hs);
        }

        // populate heating systems where at least one  of its components DOES NOT HAVE all mandatory fields set
        if (!spotIds.every((spotId: string) => this.spotIdsWithCompletedMetadata.includes(spotId))) {
          heatingSystems.incomplete.push(hs);
        }
      });

      if (this.filters.metadataStatus === 'MANDATORY_INFORMATION_COMPLETE') {
        return heatingSystems.complete;
      } else if (this.filters.metadataStatus === 'MANDATORY_INFORMATION_INCOMPLETE') {
        return heatingSystems.incomplete;
      }

      return this.heatingSystems;
    },
    filterComponentsByStatus(): HeatingSystemComponent[] {
      // AT regulation filter handling
      if (this.showATRegulationControl) {
        // Start with allowed component types, then only keep components
        // that have editable registers when AT control is active.
        let components = this.heatingSystemsComponents.filter((item) =>
          this.allowedComponentTypes().includes(item.type),
        );

        components = components.filter((c) => !!c.hasEditableRegister);

        if (this.filters.metadataStatus === ATRegulationMetadataStatus.TRUE) {
          return components.filter((c) => this.atRegulationConfigStatusPerComponent?.[c.value] === true);
        }

        if (this.filters.metadataStatus === ATRegulationMetadataStatus.FALSE) {
          return components.filter((c) => this.atRegulationConfigStatusPerComponent?.[c.value] === false);
        }

        return components;
      }

      // exclude non-mandatory components from filtered list (GK4 behavior)
      const components = this.heatingSystemsComponents.filter(
        (item) =>
          !this.nonMandatoryComponentTypes.includes(item.type) && this.allowedComponentTypes().includes(item.type),
      );
      if (this.filters.metadataStatus === 'MANDATORY_INFORMATION_COMPLETE') {
        return components.filter((item) => this.spotIdsWithCompletedMetadata.includes(item.value));
      }

      return components.filter((item) => !this.spotIdsWithCompletedMetadata.includes(item.value));
    },
    async onHeatingSystemComponentChange(id: string) {
      const activeComponent = this.heatingSystemsComponents.find((item) => item.value === id);
      this.heatingSystemsComponents.forEach((component) => {
        component.active = component.value === id ? true : false;
      });
      // close "apply settings" section
      this.$store.commit('openApplySettings', false);

      if (activeComponent?.hasEditableRegister) {
        await this.$store.dispatch('loadAtControlRegulationConfig', {
          spotId: activeComponent.value,
          apolloClient: this.$apollo,
        });
      }
      this.setActiveComponentMetadata();
    },
    setActiveComponentMetadata(): void {
      const spotId = this.activeHeatingSystemComponent?.value;
      const metadataObj = this.heatingSystemsMetadata?.find((metadata) => metadata.spotId === spotId);
      if (metadataObj) {
        this.metadata = metadataObj.metadata as HeatingSystemMetadataKey;
      } else {
        this.metadata = {} as HeatingSystemMetadataKey;
      }
    },
    async fetchHeatingSystemsMetadata(): Promise<HeatingSystemMetadataResponse[] | null> {
      if (this.showATRegulationControl) {
        return null;
      }
      try {
        this.isLoadingMetadata = true;
        const res = await this.$store.dispatch('getAllHeatingSystemMetadata');
        if (!res.ok) {
          return null;
        }

        const metadata = await res.json();
        this.$store.commit('setHeatingSystemMetadata', metadata);

        return metadata;
      } catch (error) {
        return null;
      } finally {
        this.isLoadingMetadata = false;
      }
    },
    // check if there was a heating system passed via params and select it.
    checkParams(): void {
      const heatingSystemId = this.$route.query['hsid'];
      if (heatingSystemId && isString(heatingSystemId)) {
        this.onHeatingSystemChange(heatingSystemId);
      }
    },
    resetHeatingSystems(): void {
      this.heatingSystems.forEach((hs) => {
        hs.active = false;
      });
    },
    resetComponents(): void {
      this.heatingSystemsComponents.forEach((hs) => {
        hs.active = false;
      });
    },
    async onFormSaved() {
      await this.fetchHeatingSystemsMetadata();
      this.setActiveComponentMetadata();
    },
    async fetchAlertSummariesByHeatingSystem(
      heatingSystems: HeatingSystemList[],
    ): Promise<AlertHeatingSystemSummaries_statuses[] | []> {
      try {
        this.summariesLoading = true;
        const heatingSystemIds = heatingSystems.map((hs) => hs.id);

        const variables: AlertHeatingSystemSummariesVariables = { hscIds: heatingSystemIds };
        const { data: alertTicketsData } = await this.$apollo.query({
          query: alertHeatingSystemSummariesQuery,
          client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
          variables,
          fetchPolicy: 'no-cache',
        });

        return alertTicketsData?.statuses || [];
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        return [];
      } finally {
        this.summariesLoading = false;
      }
    },
    async getEditableRegisterNames(): Promise<void> {
      if (this.$route.path.includes('/advanced')) {
        this.customerId = this.selectedCustomerId;
      }
      if (!this.customerId) {
        this.customerId = await this.fetchCustomerForTreeNode();
        if (!this.customerId) return;
      }
      try {
        const { data } = await this.$apollo.query<ModbusMappings>({
          query: modbusMappingsQuery,
          fetchPolicy: 'no-cache',
          variables: {
            customerId: this.customerId,
          },
        });
        this.editableRegisterNames = Array.from(
          new Set(
            data.modbusMappingConfig
              .flatMap((item: ModbusMappings_modbusMappingConfig) => item.mappingRules)
              .filter((rule) => rule.tableData?.includes('edit'))
              .map((rule) => rule.registerName.toLowerCase()),
          ),
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch decoder configs for heating control devices:', error);
      }
    },
    async heatingSystemSummary(heatingSystems: HeatingSystemList[]): Promise<HeatingSystemSummary[]> {
      const alertSummarries = await this.fetchAlertSummariesByHeatingSystem(heatingSystems);
      const summaries: HeatingSystemSummary[] = [];
      for (const item of heatingSystems) {
        const status = alertSummarries.find((alert: AlertHeatingSystemSummaries_statuses) => alert.id == item.id);
        const summary: HeatingSystemSummary = {
          name: item.name,
          health: status?.health ?? HeatingSystemHealth.OK,
          statusText: status?.statusText ?? '',
          statuses: status?.statuses ?? [],
          street: item.street,
          heatingType: item.heatingType,
          boilerDevice: item.boilerDevice,
          boilerModel: item.boilerModel,
          productSerial: item.productSerial,
          manufacturer: item.manufacturer,
          boilerManufacturer: item.boilerManufacturer,
          energyOutputRange: item.energyOutputRange,
          waterStorageType: item.waterStorageType,
          waterStorageModel: item.waterStorageModel,
          waterStorageManufacturer: item.waterStorageManufacturer,
          waterStorageCapacity: item.waterStorageCapacity,
          maintenanceCompany: item.maintenanceCompany,
          numApartmentUnits: item.numCommercialUnits,
          numCommercialUnits: item.numCommercialUnits,
          suppliedQuantity: item.suppliedQuantity,
          lastHydraulicBalancing: item.lastHydraulicBalancing,
          consumptions: item.heatingSystemConsumptions,
          components: item,
          siteId: item.siteId,
        };

        summaries.push(summary);
      }

      return summaries;
    },
    async fetchHeatingSystemsList(): Promise<void> {
      this.summariesLoading = true;
      try {
        const treeNodeId = this.$route.path.includes('/advanced') ? this.selectedCustomerId : this.treeNodeId;
        if (!treeNodeId) return;

        const { data } = await this.$apollo.query({
          query: heatingSystemsListQuery,
          client: APOLLO_CLIENT.HEATING_SYSTEM_COLLECTOR_CLIENT,
          fetchPolicy: 'no-cache',
          variables: {
            customerIdOrSiteId: treeNodeId,
            skip: this.skip,
            take: 500,
          },
        });

        // filter out items with empty heatingSystemMeasurementGroups
        const filteredHeatingSystems = data.heatingSystemsList?.items?.filter(
          (hs: HeatingSystemCollectionWithPaginationQuery_heatingSystemsList_items) =>
            hs.heatingSystemMeasurementGroups.length > 0,
        );

        this.heatingSystemsData = await this.heatingSystemSummary(filteredHeatingSystems);
        // If AT regulation filter is active, compute spotIds and fetch their statuses once
        if (this.showATRegulationControl) {
          if (!this.editableRegisterNames.length) {
            await this.getEditableRegisterNames();
          }
          // Use the freshly computed heatingSystemsData (already contains components) to derive spotIds
          // and also extract heating systems that have editable registers so we can store them centrally.
          const { spotIds, heatingSystems } = getHeatingSystemsAndSpots(
            this.heatingSystemsData as unknown as HeatingSystem[],
            this.editableRegisterNames,
          );

          // Commit filtered heating systems (those with editable registers) into the store
          const mappedHeatingSystems = heatingSystems.map((item) => ({ ...item, active: false }));
          this.$store.commit('setHeatingSystems', mappedHeatingSystems);

          if (spotIds && spotIds.size > 0) {
            await this.$store.dispatch('getATRegulationConfigStatuses', {
              spotIds: Array.from(spotIds),
              apolloClient: this.$apollo,
            });
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        this.summariesLoading = false;
      }
    },
    async fetchCustomerForTreeNode(): Promise<string | null> {
      if (!this.treeNodeId) return null;
      try {
        const result = await this.$apollo.query<GetTreeNodeCustomerId, GetTreeNodeCustomerIdVariables>({
          client: APOLLO_CLIENT.PORTAL_CLIENT,
          query: getTreeNodeCustomerIdQuery,
          variables: {
            id: this.treeNodeId,
          },
          fetchPolicy: 'no-cache',
        });
        const customerId = result.data.treeNodes.first?.customer?.id || null;
        return customerId;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch customer for tree node:', error);
        return null;
      }
    },
    allowedComponentTypes(): string[] {
      return [
        'BOILER',
        'BUFFER',
        'HEATING_CIRCUIT',
        'TELE_HEATING',
        'WARM_HEATING',
        'COMBINED_HEAT_POWER_PLANT',
        'HEAT_PUMP_PRIMARY',
        'HEAT_PUMP_SECONDARY',
        'HEAT_EXCHANGER_PRIMARY',
        'HEAT_EXCHANGER_SECONDARY',
        'HEATING_CONTROL',
      ];
    },
  },
});
